import { useState, useCallback, useEffect } from 'react';

/**
 * useBiometricAuth - Hook for handling biometric authentication
 * 
 * Uses the Web Authentication API (WebAuthn) for biometric auth,
 * with fallback to device passcode when biometric fails.
 * 
 * Note: WebAuthn requires HTTPS and user gesture to work.
 * On iOS/Safari, this works with Face ID/Touch ID.
 * On Android Chrome, this works with fingerprint/face unlock.
 */
const useBiometricAuth = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);

  // Check if WebAuthn is supported
  useEffect(() => {
    const checkSupport = async () => {
      // Check for WebAuthn support
      const webauthnSupported = !!(
        window.PublicKeyCredential &&
        navigator.credentials &&
        typeof navigator.credentials.create === 'function'
      );
      
      setIsSupported(webauthnSupported);
      
      // Check if platform authenticator (biometric) is available
      if (webauthnSupported && window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
        try {
          const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setIsEnrolled(available);
        } catch (e) {
          console.warn('Could not check platform authenticator:', e);
          setIsEnrolled(false);
        }
      }
    };
    
    checkSupport();
  }, []);

  /**
   * Enroll biometric credentials for the current user
   * Must be called after a successful login
   */
  const enrollBiometric = useCallback(async (userId) => {
    if (!isSupported) {
      throw new Error('Biometric authentication is not supported on this device');
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      // Generate a challenge (in production, this should come from the server)
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      const publicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'Forget Me Not',
          id: window.location.hostname,
        },
        user: {
          id: Uint8Array.from(userId, c => c.charCodeAt(0)),
          name: userId,
          displayName: 'Forget Me Not User',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Use device's built-in authenticator
          userVerification: 'required', // Require biometric/PIN
          residentKey: 'preferred',
        },
        timeout: 60000,
        attestation: 'none',
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      });

      if (credential) {
        // Store credential ID for later authentication
        const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        localStorage.setItem('biometric_credential_id', credentialId);
        localStorage.setItem('biometric_enrolled', 'true');
        setIsEnrolled(true);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Biometric enrollment failed:', err);
      setError(err.message || 'Failed to enroll biometric');
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, [isSupported]);

  /**
   * Authenticate using biometric
   * Returns true if authentication succeeds
   */
  const authenticate = useCallback(async () => {
    if (!isSupported) {
      throw new Error('Biometric authentication is not supported');
    }

    const credentialIdB64 = localStorage.getItem('biometric_credential_id');
    
    setIsAuthenticating(true);
    setError(null);

    try {
      // Generate a challenge
      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);

      const publicKeyCredentialRequestOptions = {
        challenge,
        timeout: 60000,
        userVerification: 'required',
        rpId: window.location.hostname,
      };

      // If we have a stored credential, use it
      if (credentialIdB64) {
        const credentialId = Uint8Array.from(atob(credentialIdB64), c => c.charCodeAt(0));
        publicKeyCredentialRequestOptions.allowCredentials = [{
          id: credentialId,
          type: 'public-key',
          transports: ['internal'],
        }];
      }

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      });

      if (assertion) {
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Biometric authentication failed:', err);
      setError(err.message || 'Authentication failed');
      
      // Check if user cancelled
      if (err.name === 'NotAllowedError') {
        throw new Error('Authentication was cancelled or denied');
      }
      
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, [isSupported]);

  /**
   * Remove biometric enrollment
   */
  const unenroll = useCallback(() => {
    localStorage.removeItem('biometric_credential_id');
    localStorage.removeItem('biometric_enrolled');
    setIsEnrolled(false);
  }, []);

  /**
   * Check if biometric is enrolled for current device
   */
  const checkEnrollment = useCallback(() => {
    return localStorage.getItem('biometric_enrolled') === 'true';
  }, []);

  return {
    isSupported,
    isEnrolled: isEnrolled && checkEnrollment(),
    isAuthenticating,
    error,
    enrollBiometric,
    authenticate,
    unenroll,
    checkEnrollment,
  };
};

export default useBiometricAuth;
